(function(window, progressBarControlViewFactory) {
   'use strict';

   var namespace = window.TSC = window.TSC || {};
   namespace.ProgressBarControlView = progressBarControlViewFactory(
      window,
      window.$,
      namespace.templates,
      namespace.events,
      namespace.localizationStrings,
      namespace.keys,
      namespace.mouseButtons,
      namespace.tabIndex,
      namespace.highlightDisabler,
      namespace.timeFormatter,
      namespace.playerConfiguration,
      namespace.quizController,
      namespace.supportedPluginViewElements);
})(this, function(window, $, templates, events, localizationStrings, keys, mouseButtons, tabIndex, highlightDisabler, timeFormatter, playerConfiguration, quizController, supportedPluginViewElements) {
   'use strict';

   var SKIP_TIME = 5;
   var SHIFT_SKIP_TIME = 30;

   var ProgressBarControlView = function($container, mediaElement, quizModel) {
      var markup = templates.progress_bar_control_template({
         progressIndex: tabIndex.Progress,
         accessBtnProgressBar: localizationStrings.strings.accessBtnProgressBar
      });
      $container.find('.progress-bar-control').html(markup);

      var _$document = $(window.document);
      var _requestAnimationId = null;
      var _scrubbing = false;
      var _lastScrubTime = 0;
      var _wasPlayingBeforeSeek = false;
      var _quizModel = quizModel;
      var _$loadProgressBar = $container.find('.load-progress');
      var _$playProgressBar = $container.find('.play-progress');
      var _$playTimeDisplay = $container.find('.play-time');
      var _$scrubBar = $container.find('.progress-scrubbar-track');
      var _$quizMarkers = $container.find('.markers');
      var _$pluginContainer = $container.find('.plugin-container');
      var _markers = [];
      var _nextQuizTime;
      var _keysEnabled = true;
      var _durationDisplayInitialized = false;
      var _markersCreated = false;
      var _formattedDuration;
      var _lastUpdatedFormattedMediaTime = -1;
      var _lastUpdatedScrubTime = -1;

      var shouldRespectQuizMarkers = function() {
         return _quizModel && quizController.quizEnabled && !playerConfiguration.ignoreQuizQuestions;
      };

      var updateLoadProgress = function(progress) {
         _$loadProgressBar.width(progress);
      };

      var onDownSeek = function(e) {
         e.preventDefault();
         if (_scrubbing) {
            return;
         }

         if (!_nextQuizTime) {
            allowSkipQuestionSetMarkersBeforeTime(mediaElement.currentTime);
         }

         _$document.on('pointermove touchmove', onMoveSeek);
         // Safari needs mouseup, Safari reports wrong event.buttons code after user
         // right clicks on player.  This causes pepjs to report pointerup event as pointermove event
         _$document.on('pointerup mouseup touchend mouseleave', onUpSeek);

         if (!mediaElement.paused) {
            _wasPlayingBeforeSeek = true;
            mediaElement.pause();
         } else {
            _wasPlayingBeforeSeek = false;
         }

         setScrubbing(true);
         _lastUpdatedScrubTime = -1;

         setPlayProgress(getClickX(e.originalEvent));
      };

      var onMoveSeek = function(e) {
         if (!_scrubbing) {
            return;
         }

         if (!mediaElement.paused) {
            mediaElement.pause();
         }

         setPlayProgress(getClickX(e.originalEvent));
      };

      var onUpSeek = function() {
         if (!_scrubbing) {
            return;
         }

         _$document.off('pointermove touchmove', onMoveSeek);
         _$document.off('pointerup mouseup touchend mouseleave', onUpSeek);

         var atVideoEndTime = Number(mediaElement.currentTime.toFixed(2)) >= Number(mediaElement.duration.toFixed(2));
         if (_wasPlayingBeforeSeek && !atVideoEndTime) {
            mediaElement.play();
         }

         setScrubbing(false);
      };

      var getClickX = function(event) {
         if (event.targetTouches && event.targetTouches[0] && event.targetTouches[0].pageX) {
            return event.targetTouches[0].pageX;
         }

         return event.pageX;
      };

      var setPlayProgress = function(clickX) {
         var clickRelPos = clickX - _$scrubBar.offset().left;
         var playRatio = Math.max(Math.min(clickRelPos / _$scrubBar.width(), 1), 0);

         var newTime = mediaElement.duration * playRatio;

         if (newTime < _lastScrubTime) {
            allowSkipQuestionSetMarkersBeforeTime(newTime);
         }

         // Stop scrubber from passing first quiz
         if ((_nextQuizTime || _nextQuizTime === 0) && _nextQuizTime < newTime) {
            newTime = _nextQuizTime;
         }

         _lastScrubTime = newTime;
      };

      var updatePlayProgress = function() {
         if (mediaElement.duration === 0) {
            return;
         }

         if (_scrubbing) {
            if (_lastScrubTime === _lastUpdatedScrubTime || _lastScrubTime < 0) {
               return;
            }

            _lastUpdatedScrubTime = _lastScrubTime;
            $container.trigger(events.Media.SeekToTime, _lastScrubTime);
            updateUi(_lastScrubTime);
         } else {
            if (mediaElement.seeking) {
               return;
            }
            updateUi(mediaElement.currentTime);
         }
      };

      var updateUi = function(currentTime) {
         var percentPlayed = currentTime * 100 / mediaElement.duration;
         _$playProgressBar.width(percentPlayed + '%');

         updateTimeDisplay(currentTime);
      };

      var updateTimeDisplay = function(currentTime) {
         var formattedCurrentTime = timeFormatter.formatTime(currentTime);
         if (formattedCurrentTime === _lastUpdatedFormattedMediaTime && _durationDisplayInitialized) {
            return;
         }

         _lastUpdatedFormattedMediaTime = formattedCurrentTime;

         var progressText = formattedCurrentTime;
         var progressDisplayText = formattedCurrentTime;

         if (!_durationDisplayInitialized && mediaElement.duration) {
            _durationDisplayInitialized = true;
            _formattedDuration = timeFormatter.formatTime(mediaElement.duration);
            _$scrubBar.attr('aria-valuemax', mediaElement.duration);
         }

         if (_durationDisplayInitialized) {
            progressText += ' of ' + _formattedDuration;
            progressDisplayText += ' / ' + _formattedDuration;
         }

         _$playTimeDisplay.html(progressDisplayText);
         _$scrubBar.attr('aria-valuenow', currentTime);
         _$scrubBar.attr('aria-valuetext', progressText);
      };

      var setMarkersXPos = function() {
         var mediaDurationInMilliseconds = mediaElement.duration * 1000;
         var xPos = 0;
         var timePercent = 0;
         var quizMarkerContainerWidth = _$quizMarkers.width();
         var quizMarkerContainerHeight = _$quizMarkers.height();

         _quizModel.questionSetMarkerTimes.forEach(function(questionSetMarker, i) {
            timePercent = questionSetMarker.startTime / mediaDurationInMilliseconds;
            xPos = Math.round(quizMarkerContainerWidth * timePercent - quizMarkerContainerHeight / 2);

            var marker = _markers[i];
            marker && marker.css('left', xPos + 'px');
         });
      };

      var updateQuizMarkers = function() {
         if (!shouldRespectQuizMarkers()) {
            return;
         }

         if (!_markersCreated) {
            createQuizMarkers();
         }

         updateMarkerVisibility();
      };

      var updateMarkerVisibility = function() {
         _quizModel.questionSetMarkerTimes.forEach(function(questionSetMarker, i) {
            if (questionSetMarker.completed) {
               var marker = _markers[i];
               marker && marker.hide();
            }
         });

         setMarkersXPos();
      };

      var createQuizMarkers = function() {
         if (!shouldRespectQuizMarkers()) {
            return;
         }

         _markersCreated = true;

         _quizModel.questionSetMarkerTimes.forEach(function() {
            _markers.push($('<div>').addClass('quiz-marker'));
         });

         _$quizMarkers.append(_markers);
      };

      var setScrubbing = function(value) {
         _scrubbing = value;
         if (_scrubbing) {
            $container.trigger(events.Scrubbing.Started);
         } else {
            $container.trigger(events.Scrubbing.Ended);
         }
      };

      var keyPressed = function(e) {
         if (!_keysEnabled) {
            return;
         }

         var timeToSkip = e.shiftKey ? SHIFT_SKIP_TIME : SKIP_TIME;
         if (keys.isKeyOfType(e.key, keys.keyTypes.rightArrow)) {
            mediaElement.currentTime = getSafeSeekTime(mediaElement.currentTime + timeToSkip);
         } else if (keys.isKeyOfType(e.key, keys.keyTypes.leftArrow)) {
            mediaElement.currentTime = getSafeSeekTime(mediaElement.currentTime - timeToSkip);
         } else if (keys.isKeyOfType(e.key, keys.keyTypes.space)) {
            if (mediaElement.paused) {
               mediaElement.play();
            } else {
               mediaElement.pause();
            }
         }
      };

      var onMousedown = function(e) {
         if (e.button === mouseButtons.mainButton) {
            onDownSeek(e);
         } else {
            highlightDisabler.disableHighlights($(this));
         }
      };

      var disableKeyListeners = function() {
         _$scrubBar.attr('tabindex', tabIndex.Disabled);
         _keysEnabled = false;
      };

      var enableKeyListeners = function() {
         _$scrubBar.attr('tabindex', tabIndex.Progress);
         _keysEnabled = true;
      };

      var getXPositionForQuestionSetMarker = function(questionSetMarkerId) {
         if (!shouldRespectQuizMarkers()) {
            return;
         }

         var questionSetMarker = null;
         for (var i = 0; i < _quizModel.questionSetMarkerTimes.length; i++) {
            questionSetMarker = quizModel.questionSetMarkerTimes[i];
            if (questionSetMarker.questionSetIndex === questionSetMarkerId) {
               var timePercent = questionSetMarker.startTime / (mediaElement.duration * 1000);
               var measuredContainerWidth = $container.width();
               var measuredQuizMarkerWidth = _$quizMarkers.width();
               // use the max width, when control is hidden width measurement is not correct.
               var usedElementWidth = Math.max(measuredContainerWidth, measuredQuizMarkerWidth);
               return Math.round(usedElementWidth * timePercent) + _$scrubBar.offset().left;
            }
         }
      };

      var allowSkipQuestionSetMarkersBeforeTime = function(timeInSeconds) {
         if (!shouldRespectQuizMarkers()) {
            return;
         }

         var foundNextMarker = false;
         var timeInMilliseconds = timeInSeconds * 1000;
         var nextQuizTime = null;
         _quizModel.questionSetMarkerTimes.forEach(function(questionSetMarker) {
            questionSetMarker.canSeekPast = questionSetMarker.startTime < timeInMilliseconds;

            if (questionSetMarker.startTime >= timeInMilliseconds && !questionSetMarker.canSeekPast && !foundNextMarker) {
               foundNextMarker = true;
               nextQuizTime = questionSetMarker.startTime / 1000;
            }
         });

         _nextQuizTime = nextQuizTime;
      };

      var getSafeSeekTime = function(timeInSeconds) {
         // question sets
         var timeInMilliseconds = timeInSeconds * 1000;
         var questionSetMarker;
         if (shouldRespectQuizMarkers()) {
            for (var i = 0; i < _quizModel.questionSetMarkerTimes.length; i++) {
               questionSetMarker = _quizModel.questionSetMarkerTimes[i];
               if (!questionSetMarker.canSeekPast && questionSetMarker.startTime < timeInMilliseconds) {
                  return questionSetMarker.startTime / 1000;
               }
            }
         }

         // video time bounds
         if (timeInSeconds < 0) {
            return 0;
         }
         if (timeInSeconds > mediaElement.duration) {
            return mediaElement.duration;
         }
         return timeInSeconds;
      };

      var onQuizzingCompleteHandler = function() {
         allowSkipQuestionSetMarkersBeforeTime(mediaElement.currentTime);
      };

      var initPlugins = function() {
         var pluginController = playerConfiguration.getPluginController();
         if (!pluginController) {
            return;
         }
         pluginController.initializePluginsForViewElement(supportedPluginViewElements.PROGRESS_BAR, _$pluginContainer);
      };

      var setup = function() {
         _$scrubBar.attr('aria-valuemin', 0);
         _$scrubBar.on('keydown', keyPressed);
         _$scrubBar.on('touchstart', onDownSeek);
         _$scrubBar.on('mousedown', onMousedown);
         $container.on(events.Controls.Disable, disableKeyListeners);
         $container.on(events.Controls.Enable, enableKeyListeners);
         $container.on(events.Quizzing.Complete, onQuizzingCompleteHandler);

         var onRequestAnimationFrame = function() {
            updatePlayProgress();
            _requestAnimationId = window.requestAnimationFrame(onRequestAnimationFrame);
         };

         _requestAnimationId = window.requestAnimationFrame(onRequestAnimationFrame);

         initPlugins();
      };

      var destroy = function() {
         _$scrubBar.off('keydown', keyPressed);
         _$scrubBar.off('touchstart', onDownSeek);
         _$scrubBar.off('mousedown', onMousedown);
         $container.off(events.Controls.Disable, disableKeyListeners);
         $container.off(events.Controls.Enable, enableKeyListeners);
         $container.off(events.Quizzing.Complete, onQuizzingCompleteHandler);

         if (_requestAnimationId !== null) {
            window.cancelAnimationFrame(_requestAnimationId);
         }
      };

      var durationHasChanged = function() {
         _durationDisplayInitialized = false;
      };

      setup();

      return Object.defineProperties({
         getSafeSeekTime: getSafeSeekTime,
         getXPositionForQuestionSetMarker: getXPositionForQuestionSetMarker,
         updateQuizMarkers: updateQuizMarkers,
         updateLoadProgress: updateLoadProgress,
         allowSkipQuestionSetMarkersBeforeTime: allowSkipQuestionSetMarkersBeforeTime,
         durationHasChanged: durationHasChanged,
         destroy: destroy
      }, {
         scrubbing: {
            get: function() {
               return _scrubbing;
            },
            set: setScrubbing
         }, lastScrubTime: {
            get: function() {
               return _lastScrubTime;
            }
         },
         controlElement: {
            get: function() {
               return _$scrubBar;
            }
         }
      });
   };

   return {
      create: ProgressBarControlView
   };
});
